import { useCallback, useEffect, useState, type ReactNode } from "react"
import { Link, useParams } from "react-router-dom"
import { api } from "../../lib/api"
import type { Produto, QuestaoRevisada, Resultado as ResultadoTipo, ResultadoUnidade } from "../../types"
import { ROTULO, fraseDeProntidao } from "../../lib/prontidao"
import { subDoAprovado, subDoDiagnostico } from "../../lib/momento"
import { Barra, Card, Carregando, Selo } from "../ui"
import { QuestaoRevisadaCard } from "./QuestaoRevisada"
import {
  Caminho,
  ChamadaFinal,
  Continuidade,
  Objecoes,
  Oferta,
  OfertaResumida,
  ProvaDoMecanismo,
  useCheckoutInline,
} from "./Preparacao"

/*
 * A tela de resultado olha para a frente. A antiga era um relatório de
 * desempenho seguido de um paywall — "não está pronto", cinco barras vazias,
 * "fechar as 5 unidades", preço — e a pessoa saía com a sensação de estar
 * longe. Esta lê o mesmo resultado como mapa: diagnóstico → ponto de partida →
 * caminho → prova do mecanismo → oferta → objeções → chamada.
 *
 * Nada de regra mudou: o que sai no payload continua decidido pelo servidor
 * (gabarito, amostra, retidas), e o único GET ao resultado é o que carimba
 * `gabarito_visto`. Quem já pagou não vê oferta — vê o próximo passo.
 */

// Unidades em que a pessoa perdeu ponto, da mais fraca para a menos fraca. É a
// lista INTEIRA: citar só as primeiras enquanto a tabela mostrava mais fazia
// parecer que o sistema não tinha visto as outras.
function unidadesAReforcar(resultado: ResultadoTipo): ResultadoUnidade[] {
  return [...resultado.por_unidade]
    .filter((u) => u.total > 0 && u.acertos < u.total)
    .sort((a, b) => a.acertos / a.total - b.acertos / b.total)
}

function unidadesFechadas(resultado: ResultadoTipo): ResultadoUnidade[] {
  return resultado.por_unidade.filter((u) => u.total > 0 && u.acertos === u.total)
}

// A questão da demonstração: uma errada, da unidade mais fraca — o mesmo
// critério do servidor para a amostra. Só cai numa certa se não houve erro
// (e aí o que se demonstra é o comentário, não a correção).
function escolherDemonstracao(visiveis: QuestaoRevisada[], fracas: ResultadoUnidade[]): QuestaoRevisada | null {
  if (visiveis.length === 0) return null
  const fraqueza = new Map(fracas.map((u, i) => [u.codigo, i]))
  const erradas = [...visiveis]
    .filter((q) => q.correta !== true)
    .sort(
      (a, b) =>
        (fraqueza.get(a.unidade.codigo) ?? 99) - (fraqueza.get(b.unidade.codigo) ?? 99) || a.posicao - b.posicao
    )
  return erradas[0] ?? visiveis[0]
}

export default function Resultado() {
  const { id } = useParams<{ id: string }>()
  const [resultado, setResultado] = useState<ResultadoTipo | null>(null)
  const [produto, setProduto] = useState<Produto | null>(null)
  // O degrau acima, só no resultado de Motonauta: a preparação completa do
  // Arrais, que inclui o Motonauta. No resultado de Arrais ela JÁ É o produto
  // da página, e este fica nulo — senão a oferta apareceria duas vezes.
  const [pacote, setPacote] = useState<Produto | null>(null)

  const carregar = useCallback(() => {
    api.get<ResultadoTipo>(`/tentativas/${id}/resultado`).then((r) => {
      setResultado(r)
      // O preço vem do banco, nunca do texto — e já personalizado (renovação
      // paga menos). Só busca quando a oferta vai aparecer, e sem `tela=acesso`:
      // mostrar o preço não é ver o checkout. `view_item` só conta quando o Pix
      // abre de fato (ver useCheckoutInline).
      if (!r.acesso_vigente) {
        api
          .get<{ produtos: Produto[] }>("/produtos")
          .then((p) => {
            setProduto(p.produtos.find((x) => x.slug === r.habilitacao_slug) ?? null)
            setPacote(p.produtos.find((x) => x.pacote && !x.acesso_vigente && x.slug !== r.habilitacao_slug) ?? null)
          })
          .catch(() => {
            setProduto(null)
            setPacote(null)
          })
      }
    })
  }, [id])

  useEffect(() => {
    carregar()
  }, [carregar])

  // Pagou: recarrega o resultado, que agora vem com acesso vigente e o gabarito
  // inteiro — a pessoa vê o que comprou sem sair da tela.
  const checkout = useCheckoutInline(resultado?.habilitacao_slug ?? "", produto, pacote, carregar)

  if (!resultado) return <Carregando texto="Corrigindo…" />

  const gratis = !resultado.acesso_vigente
  const fechadas = unidadesFechadas(resultado)
  const fracas = unidadesAReforcar(resultado)

  // O que a pessoa pode ver comentado nesta tentativa: tudo, se o gabarito
  // está liberado; senão, a amostra que o servidor escolheu.
  const visiveis = resultado.gabarito_liberado ? resultado.questoes : resultado.amostra
  const demo = gratis ? escolherDemonstracao(visiveis, fracas) : null
  const restantes = visiveis.filter((q) => q !== demo)

  return (
    <div className="space-y-8">
      <Hero resultado={resultado} />
      <PontoDePartida resultado={resultado} fechadas={fechadas} fracas={fracas} />

      {gratis ? (
        <>
          <Caminho resultado={resultado} />
          <OfertaResumida resultado={resultado} produto={produto} pacote={pacote} checkout={checkout} />
          {demo && <ProvaDoMecanismo demo={demo} />}
          <Continuidade resultado={resultado} fracas={fracas} restantes={restantes} />
          <Oferta resultado={resultado} produto={produto} pacote={pacote} checkout={checkout} />
          <Objecoes resultado={resultado} produto={produto} />
          <ChamadaFinal resultado={resultado} produto={produto} checkout={checkout} />
        </>
      ) : (
        <>
          <ProximoPasso resultado={resultado} />
          <section className="revela">
            <h2 className="mb-3 font-bold text-navy-900">Gabarito comentado</h2>
            <div className="space-y-4">
              {resultado.questoes.map((q) => (
                <QuestaoRevisadaCard key={q.questao_id} q={q} />
              ))}
            </div>
          </section>
          <div className="flex flex-col justify-center gap-3 pb-8 sm:flex-row">
            <Link
              to="/painel"
              className="rounded-xl bg-gold-500 px-6 py-3 text-center text-sm font-bold text-navy-900 hover:bg-gold-600"
            >
              Continuar minha preparação
            </Link>
            <Link
              to="/historico"
              className="rounded-xl border border-navy-200 bg-white px-6 py-3 text-center text-sm font-bold text-navy-800"
            >
              Ver histórico
            </Link>
          </div>
        </>
      )}
    </div>
  )
}

/**
 * A manchete não é a nota nem o veredito. "4/10" em letra gigante é feedback
 * sobre a pessoa (Kluger e DeNisi, 1996); "não está pronto" é sentença sobre
 * uma amostra de seis questões. O que o diagnóstico produziu de útil é o
 * ponto de partida — e é isso que a manchete diz. A nota continua na tela,
 * honesta e por extenso, como linha de apoio.
 */
function Hero({ resultado }: { resultado: ResultadoTipo }) {
  const { acertos, total, acertos_para_aprovar: corte } = resultado
  // "Diagnóstico" é a curta. Um simulado no formato da prova revisto com o
  // acesso vencido continua sendo simulado — a oferta muda, a leitura da nota não.
  const diagnostico = !resultado.acesso_vigente && resultado.modo === "curta"

  const eyebrow = diagnostico ? `Seu diagnóstico está pronto · ${resultado.prova}` : resultado.prova

  const manchete = diagnostico
    ? resultado.aprovada
      ? "Passou no diagnóstico. Agora sabemos por onde seguir."
      : "Agora sabemos por onde começar."
    : resultado.aprovada
      ? "Passou neste simulado."
      : "Não passou neste simulado."

  const sub = diagnostico
    ? resultado.aprovada
      ? subDoAprovado(resultado)
      : (subDoDiagnostico(resultado.momento) ?? "Esse resultado não é sua nota final. É seu ponto de partida.")
    : null

  const fato = diagnostico
    ? `Você acertou ${acertos} de ${total} ${total === 1 ? "questão" : "questões"} neste diagnóstico. Para a linha de corte daqui, ${corte === 1 ? "seria 1 acerto" : `seriam ${corte} acertos`}.`
    : `Você acertou ${acertos} de ${total}. Para passar, precisava de ${corte}.`

  return (
    <Card className={`revela ${resultado.aprovada ? "border-emerald-300" : ""}`}>
      <div className="space-y-2 text-center">
        <p className="font-mono text-[0.65rem] uppercase tracking-[0.22em] text-gold-600">{eyebrow}</p>
        <h1 className="text-2xl font-extrabold leading-tight text-navy-900 sm:text-3xl">{manchete}</h1>
        {sub && <p className="text-navy-800">{sub}</p>}
        <p className="text-sm text-navy-800">{fato}</p>
        {/* Normaliza a reprovação para quem está no grátis, numa linha só: a
            evidência (Richland, Kornell e Kao, 2009) mora na FAQ da landing;
            aqui, na primeira dobra, ela pesava mais do que ajudava. */}
        {diagnostico && !resultado.aprovada && !subDoDiagnostico(resultado.momento) && (
          <p className="text-sm text-slate-500">
            O objetivo deste diagnóstico é descobrir onde concentrar sua preparação.
          </p>
        )}
        {resultado.status === "expirada" && (
          <p className="text-sm text-slate-500">O tempo acabou — o que você respondeu foi corrigido.</p>
        )}
      </div>
    </Card>
  )
}

/**
 * Compacto de propósito: duas listas com nome, sem barra. Cinco barras "0/1"
 * dominando a tela transformavam uma amostra de uma questão por unidade em
 * cinco reprovações. O detalhe por unidade, com as barras, continua — atrás
 * de um "ver diagnóstico completo", aberto por padrão só para quem já paga e
 * veio medir de verdade.
 */
function PontoDePartida({
  resultado,
  fechadas,
  fracas,
}: {
  resultado: ResultadoTipo
  fechadas: ResultadoUnidade[]
  fracas: ResultadoUnidade[]
}) {
  const gratis = !resultado.acesso_vigente

  return (
    <section className="revela">
      <p className="font-mono text-[0.65rem] uppercase tracking-[0.22em] text-gold-600">
        {gratis ? "Ponto de partida" : "Diagnóstico"}
      </p>
      <h2 className="mt-1 text-lg font-bold leading-snug text-navy-900">
        {gratis ? "Seu ponto de partida" : "Onde você está"}
      </h2>

      <Card className="mt-4">
        <div className="grid gap-6 sm:grid-cols-2">
          <ListaCompacta
            titulo="Você já acertou"
            unidades={fechadas}
            vazio="Nenhuma unidade fechada neste diagnóstico — é por isso que ele existe."
            marcador={<span className="font-bold text-emerald-600" aria-hidden>✓</span>}
          />
          <ListaCompacta
            titulo="Vamos reforçar"
            unidades={fracas}
            vazio="Nada. Você fechou todas."
            marcador={<span className="mt-2 h-1.5 w-1.5 rounded-full bg-gold-500" aria-hidden />}
          />
        </div>

        <p className="mt-5 text-sm text-navy-800">
          {fracas.length > 0
            ? "Você não precisa estudar tudo de novo. Agora já sabemos onde concentrar sua preparação."
            : "Você fechou todas as unidades deste simulado."}
        </p>

        <details className="group mt-4 border-t border-navy-50 pt-4" open={!gratis}>
          <summary className="flex list-none items-center gap-2 text-sm font-semibold text-navy-800 [&::-webkit-details-marker]:hidden">
            <svg
              className="h-4 w-4 shrink-0 text-gold-600 transition-transform group-open:rotate-90"
              viewBox="0 0 20 20"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              <path d="M7 5l6 5-6 5" />
            </svg>
            Ver diagnóstico completo
          </summary>
          <div className="mt-4 space-y-6">
            <ListaDeUnidades titulo="Onde você já está bem" unidades={fechadas} vazio="Nenhuma unidade fechada ainda." />
            <ListaDeUnidades titulo="O que ainda falta estudar" unidades={fracas} vazio="Nada. Você fechou todas." />
          </div>
        </details>
      </Card>
    </section>
  )
}

function ListaCompacta({
  titulo,
  unidades,
  vazio,
  marcador,
}: {
  titulo: string
  unidades: ResultadoUnidade[]
  vazio: string
  marcador: ReactNode
}) {
  return (
    <div>
      <h3 className="text-xs font-semibold uppercase tracking-wider text-navy-600">{titulo}</h3>
      {unidades.length === 0 ? (
        <p className="mt-2 text-sm text-slate-500">{vazio}</p>
      ) : (
        <ul className="mt-2 space-y-1.5">
          {unidades.map((u) => (
            <li key={u.codigo} className="flex items-start gap-2 text-sm text-navy-900">
              <span className="flex w-4 shrink-0 justify-center">{marcador}</span>
              <span>{u.unidade}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

/** O detalhe por unidade de sempre: acerto sobre total e a barra que enche. */
function ListaDeUnidades({
  titulo,
  unidades,
  vazio,
}: {
  titulo: string
  unidades: ResultadoUnidade[]
  vazio: string
}) {
  return (
    <div>
      <h3 className="text-sm font-bold text-navy-900">{titulo}</h3>
      {unidades.length === 0 ? (
        <p className="mt-2 text-sm text-slate-500">{vazio}</p>
      ) : (
        <ul className="mt-3 space-y-3">
          {unidades.map((u) => (
            <li key={u.codigo} className="space-y-1">
              <div className="flex items-baseline justify-between text-sm">
                <span className="text-navy-800">
                  <span className="font-mono text-xs text-slate-500">{u.codigo}</span> {u.unidade}
                </span>
                <span className="font-semibold text-navy-900">
                  {u.acertos}/{u.total}
                </span>
              </div>
              <Barra atual={u.acertos} total={u.total} />
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

/**
 * Para quem já pagou, o lugar da oferta é o próximo passo: o critério de
 * pronto por escrito, com a frase de lib/prontidao — a mesma do painel.
 */
function ProximoPasso({ resultado }: { resultado: ResultadoTipo }) {
  const p = resultado.prontidao
  const acao = resultado.aprovada
    ? "Faça mais um no formato da prova."
    : "Volte ao gabarito das unidades em que perdeu ponto e faça mais um no formato da prova."

  return (
    <Card destaque className="revela">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="font-bold text-navy-900">Seu próximo passo</h2>
        <Selo tom={p.estado === "pronto" ? "ok" : p.estado === "em_preparacao" ? "atencao" : "neutro"}>{ROTULO[p.estado]}</Selo>
      </div>
      <p className="mt-1 text-sm text-navy-800">
        {p.estado === "pronto" ? fraseDeProntidao(p) : `${acao} ${fraseDeProntidao(p)}`}
      </p>
      <Link
        to="/painel"
        className="mt-4 inline-block rounded-xl bg-gold-500 px-6 py-3 text-sm font-bold text-navy-900 hover:bg-gold-600"
      >
        Continuar minha preparação
      </Link>
    </Card>
  )
}
